import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { effectivePriceCents, formatMoney, PLATFORM_LABELS, type Platform } from '@va/shared';
import { errorMessage } from '../api/client';
import { IconApple, IconClose, IconWindows } from './icons';

export function Spinner({ label }: { label?: string }) {
  return <span className="spinner" role="progressbar" aria-label={label ?? 'Loading'} />;
}

export function Loading({ label }: { label?: string }) {
  return (
    <div className="loading-block">
      <div className="stack center" style={{ alignItems: 'center' }}>
        <Spinner />
        {label && <span className="muted small">{label}</span>}
      </div>
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="form-error row between">
      <span>{errorMessage(error)}</span>
      {onRetry && (
        <button className="btn btn-sm btn-outline" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Price({ priceCents, salePriceCents, currency }: { priceCents: number; salePriceCents: number | null; currency: string }) {
  const eff = effectivePriceCents({ priceCents, salePriceCents });
  if (eff === 0) return <span className="price"><span className="free">Free</span></span>;
  return (
    <span className="price">
      {eff < priceCents && <s>{formatMoney(priceCents, currency)}</s>}
      {formatMoney(eff, currency)}
    </span>
  );
}

export function PlatformIcons({ platforms }: { platforms: Platform[] }) {
  return (
    <span className="platforms" title={platforms.map((p) => PLATFORM_LABELS[p]).join(' and ')}>
      {platforms.includes('win') && <IconWindows aria-label="Windows" />}
      {platforms.includes('mac') && <IconApple aria-label="macOS" />}
    </span>
  );
}

marked.setOptions({ gfm: true, breaks: false });

export function Markdown({ source, className = 'prose' }: { source: string; className?: string }) {
  const html = DOMPurify.sanitize(marked.parse(source, { async: false }) as string, { USE_PROFILES: { html: true } });
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * The account and admin menu. On phones it becomes one row of buttons you swipe sideways (see
 * .side-nav in app.css), with the current page's button scrolled into view.
 */
export function SideNav({ label, children }: { label: string; children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    const nav = ref.current;
    const active = nav?.querySelector<HTMLElement>('a.active');
    if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
  }, [pathname]);
  return (
    <nav ref={ref} className="side-nav" aria-label={label}>
      {children}
    </nav>
  );
}

export function Modal({ open, onClose, children, wide, label }: { open: boolean; onClose: () => void; children: ReactNode; wide?: boolean; label?: string }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={label}>
        <div className="row between" style={{ marginBottom: 8 }}>
          <span />
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function formatDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(undefined, opts).format(new Date(iso));
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const s = (Date.now() - Date.parse(iso)) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)} days ago`;
  return formatDate(iso);
}

export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.ceil((Date.parse(iso) - Date.now()) / 86400_000);
}
